import { ApiError, type HttpMethod, requestJson } from "@unizpay/ui";
import type {
  AcceptedInvitation,
  ApiKey,
  AuditEvent,
  CheckoutSettings,
  CheckoutSettingsInput,
  ConfirmedPayoutChange,
  CreatePaymentInput,
  CreateProfileInput,
  CreatedApiKey,
  CreatedWebhookEndpoint,
  Invitation,
  InvitationPreview,
  Me,
  Member,
  Mode,
  Network,
  Ok,
  Overview,
  OverviewDays,
  Payment,
  PaymentDetail,
  PaymentList,
  PaymentListQuery,
  Profile,
  ProfileDetails,
  ProfileDetailsInput,
  Role,
  RotatedWebhookSecret,
  SecurityCheckFields,
  SecurityCheckStart,
  SettingsInput,
  SettingsResponse,
  SettlementConfig,
  StrayFunds,
  TestEventResult,
  TransactionDetail,
  TransactionList,
  TransactionQuery,
  UserWallet,
  WalletBalance,
  WalletFee,
  WalletNetwork,
  WalletQuote,
  WalletSendInput,
  WalletTransfer,
  WalletTransferInput,
  WebhookDelivery,
  WebhookEndpoint,
} from "./types";

/** Readable marker the API sets with the session cookie (which scripts cannot see). */
const SIGNED_IN_COOKIE = "uzp_signed_in";

/** False means certainly signed out; true means a session may exist (it can still have expired). */
export function maybeSignedIn(): boolean {
  return document.cookie.split("; ").some((c) => c.startsWith(`${SIGNED_IN_COOKIE}=`));
}

/** Drops the marker once the API says the session is gone, so the next visit skips the check. */
export function clearSignedIn() {
  document.cookie = `${SIGNED_IN_COOKIE}=; Path=/; Max-Age=0`;
}

let unauthorizedHandler: (() => void) | null = null;

/** Called whenever a /dashboard-api request returns 401 (session missing or expired). */
export function setUnauthorizedHandler(fn: (() => void) | null) {
  unauthorizedHandler = fn;
}

function request<T>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
  return requestJson<T>(method, path, { body }).catch((err: unknown) => {
    // A wrong wallet PIN is a 401 too, but the session is fine.
    if (
      err instanceof ApiError &&
      err.status === 401 &&
      err.code !== "pin_incorrect" &&
      path.startsWith("/dashboard-api/")
    )
      unauthorizedHandler?.();
    throw err;
  });
}

function withQuery(path: string, params: Record<string, string | undefined>) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined) q.set(k, v);
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

const enc = encodeURIComponent;

export const api = {
  auth: {
    login: (email: string) => request<Ok>("POST", "/auth/login", { email }),
    verify: (token: string) => request<Ok>("POST", "/auth/verify", { token }),
    logout: () => request<Ok>("POST", "/auth/logout"),
  },

  /** The signed-in user, across profiles. */
  account: {
    me: () => request<Me>("GET", "/dashboard-api/me"),
    updateMe: (name: string) => request<Ok>("PATCH", "/dashboard-api/me", { name }),
    createProfile: (input: CreateProfileInput) => request<Profile>("POST", "/dashboard-api/profiles", input),
    /** What a profile can settle to in a mode; needs no profile (the profile wizard). */
    networks: (mode: Mode) => request<Network[]>("GET", withQuery("/dashboard-api/networks", { mode })),
    wallet: () => request<{ wallet: UserWallet }>("GET", "/dashboard-api/wallet"),
    /** On the wallet's networks in `mode`: mainnets in live, testnets in test. */
    walletBalances: (mode: Mode) =>
      request<{
        address: string;
        balances: WalletBalance[];
        /** Networks whose balances could not be read right now. */
        unavailable: string[];
        networks: WalletNetwork[];
      }>("GET", withQuery("/dashboard-api/wallet/balances", { mode })),
    walletTransfers: (mode: Mode) =>
      request<WalletTransfer[]>("GET", withQuery("/dashboard-api/wallet/transfers", { mode })),
    /** The fee for sending a token on a network, before an amount is typed. */
    transferFee: (chainId: string, token: string) =>
      request<WalletFee>(
        "GET",
        `/dashboard-api/wallet/send/fee?${new URLSearchParams({ chain_id: chainId, token })}`,
      ),
    quoteTransfer: (input: WalletTransferInput) =>
      request<WalletQuote>("POST", "/dashboard-api/wallet/send/quote", input),
    /** Emails the code that, with the PIN, approves exactly this send. */
    sendCode: (input: WalletTransferInput) =>
      request<SecurityCheckStart>("POST", "/dashboard-api/wallet/send/code", input),
    /** Checks the emailed code alone, before the PIN is asked for; the send uses it up. */
    verifySendCode: (input: WalletTransferInput & { check_id: string; code: string }) =>
      request<{ ok: true }>("POST", "/dashboard-api/wallet/send/code/verify", input),
    sendFromWallet: (input: WalletSendInput) =>
      request<WalletTransfer>("POST", "/dashboard-api/wallet/send", input),
    /** Emails the code that, with the current PIN, approves changing the PIN. */
    pinCode: () => request<SecurityCheckStart>("POST", "/dashboard-api/wallet/pin/code"),
    /** Checks the PIN without using it; a wrong one counts toward the lock. */
    verifyWalletPin: (pin: string) => request<Ok>("POST", "/dashboard-api/wallet/pin/verify", { pin }),
    verifyPinCode: (check_id: string, code: string) =>
      request<Ok>("POST", "/dashboard-api/wallet/pin/code/verify", { check_id, code }),
    /** Sets the first PIN, or changes it when `currentPin` is given. */
    setWalletPin: (pin: string, currentPin?: string, check?: Omit<SecurityCheckFields, "pin">) =>
      request<Ok>("PUT", "/dashboard-api/wallet/pin", {
        pin,
        ...(currentPin !== undefined && { current_pin: currentPin }),
        ...check,
      }),
    /** The link in the confirmation email; must be the user who made the change. */
    confirmPayoutChange: (token: string) =>
      request<ConfirmedPayoutChange>("POST", "/dashboard-api/payout-changes/confirm", { token }),
    /** Needs no session: the cancel link in the email works signed out. */
    cancelPayoutChange: (token: string) =>
      request<Ok>("POST", "/dashboard-api/payout-changes/cancel", { token }),
    /** Needs no session, so the invite page can describe the invitation before sign-in. */
    invitation: (token: string) =>
      request<InvitationPreview>("GET", `/dashboard-api/invitations/${enc(token)}`),
    acceptInvitation: (token: string) =>
      request<AcceptedInvitation>("POST", `/dashboard-api/invitations/${enc(token)}/accept`),
  },
};

/**
 * Endpoints of one profile, under /dashboard-api/profiles/:profileId. Payment, settlement and
 * developer calls carry `?mode=` so the session acts on test or live data; the profile and its
 * team are the same in both modes. Pages get the active profile's client from `useProfileApi()`.
 */
export function profileApi(profileId: string) {
  const base = `/dashboard-api/profiles/${enc(profileId)}`;
  const dash = (path: string, mode: Mode, params: Record<string, string | undefined> = {}) =>
    withQuery(`${base}${path}`, { mode, ...params });

  return {
    profile: () => request<ProfileDetails>("GET", `${base}/profile`),
    updateProfile: (input: ProfileDetailsInput) => request<Ok>("PATCH", `${base}/profile`, input),

    members: () => request<Member[]>("GET", `${base}/members`),
    changeRole: (userId: string, role: Role) =>
      request<Ok>("PATCH", `${base}/members/${enc(userId)}`, { role }),
    /** Removes a member, or leaves the profile when `userId` is your own. */
    removeMember: (userId: string) => request<Ok>("DELETE", `${base}/members/${enc(userId)}`),
    invitations: () => request<Invitation[]>("GET", `${base}/invitations`),
    invite: (email: string, role: Role) =>
      request<Invitation>("POST", `${base}/invitations`, { email, role }),
    revokeInvitation: (id: string) => request<Ok>("DELETE", `${base}/invitations/${enc(id)}`),
    auditEvents: () => request<AuditEvent[]>("GET", `${base}/audit-events`),

    networks: (mode: Mode) => request<Network[]>("GET", dash("/networks", mode)),
    checkoutSettings: (mode: Mode) => request<CheckoutSettings>("GET", dash("/checkout-settings", mode)),
    saveCheckoutSettings: (input: CheckoutSettingsInput) =>
      request<CheckoutSettings>("PUT", dash("/checkout-settings", input.mode), input),
    settings: (mode: Mode) => request<SettingsResponse>("GET", dash("/settings", mode)),
    // The API reads the mode for PUT /settings from the body; it is also sent as ?mode= for consistency.
    saveSettings: (input: SettingsInput) =>
      request<SettlementConfig>("PUT", dash("/settings", input.mode), input),
    /** Emails the code for the new live addresses in this change; `needed: false` when it adds none. */
    settingsCode: (input: SettingsInput) =>
      request<({ needed: true } & SecurityCheckStart) | { needed: false }>(
        "POST",
        dash("/settings/code", input.mode),
        input,
      ),
    /** Checks the emailed code alone, before the PIN is asked for. */
    verifySettingsCode: (input: SettingsInput & { check_id: string; code: string }) =>
      request<Ok>("POST", dash("/settings/code/verify", input.mode), input),
    /** Cancels the pending version, scheduled or awaiting confirmation (owners). */
    cancelPendingSettings: (mode: Mode) => request<Ok>("POST", dash("/settings/pending/cancel", mode)),
    /** Sends the confirmation email of the pending version again (the user who made the change). */
    resendPendingConfirmation: (mode: Mode) => request<Ok>("POST", dash("/settings/pending/resend", mode)),

    apiKeys: (mode: Mode) => request<ApiKey[]>("GET", dash("/api-keys", mode)),
    createApiKey: (mode: Mode, name: string, check?: SecurityCheckFields) =>
      request<CreatedApiKey>("POST", dash("/api-keys", mode), { mode, name, ...check }),
    /** Emails the code that, with the PIN, approves creating a live key with this name. */
    apiKeyCode: (mode: Mode, name: string) =>
      request<SecurityCheckStart>("POST", dash("/api-keys/code", mode), { mode, name }),
    /** Checks the emailed code alone, before the PIN is asked for. */
    verifyApiKeyCode: (mode: Mode, name: string, check_id: string, code: string) =>
      request<Ok>("POST", dash("/api-keys/code/verify", mode), { mode, name, check_id, code }),
    revokeApiKey: (mode: Mode, id: string) => request<Ok>("DELETE", dash(`/api-keys/${enc(id)}`, mode)),

    webhookEndpoints: (mode: Mode) => request<WebhookEndpoint[]>("GET", dash("/webhook-endpoints", mode)),
    webhookEndpoint: (mode: Mode, id: string) =>
      request<WebhookEndpoint>("GET", dash(`/webhook-endpoints/${enc(id)}`, mode)),
    createWebhookEndpoint: (mode: Mode, url: string) =>
      request<CreatedWebhookEndpoint>("POST", dash("/webhook-endpoints", mode), { mode, url }),
    rotateWebhookSecret: (mode: Mode, id: string) =>
      request<RotatedWebhookSecret>("POST", dash(`/webhook-endpoints/${enc(id)}/rotate`, mode)),
    sendTestEvent: (mode: Mode, id: string) =>
      request<TestEventResult>("POST", dash(`/webhook-endpoints/${enc(id)}/test`, mode)),
    deleteWebhookEndpoint: (mode: Mode, id: string) =>
      request<Ok>("DELETE", dash(`/webhook-endpoints/${enc(id)}`, mode)),
    webhookDeliveries: (mode: Mode, id: string) =>
      request<WebhookDelivery[]>("GET", dash(`/webhook-endpoints/${enc(id)}/deliveries`, mode)),

    overview: (mode: Mode, days: OverviewDays) =>
      request<Overview>("GET", dash("/overview", mode, { days: String(days) })),
    payments: (mode: Mode, q: PaymentListQuery = {}) =>
      request<PaymentList>(
        "GET",
        dash("/payments", mode, {
          status: q.status,
          source: q.source,
          q: q.q,
          starting_after: q.startingAfter,
          limit: q.limit === undefined ? undefined : String(q.limit),
        }),
      ),
    transactions: (mode: Mode, q: TransactionQuery = {}) =>
      request<TransactionList>(
        "GET",
        dash("/transactions", mode, {
          status: q.status,
          network: q.network,
          q: q.q,
          starting_after: q.startingAfter,
          limit: q.limit === undefined ? undefined : String(q.limit),
        }),
      ),
    /** CSV download of the transactions matching the filters (a plain link: the session cookie signs it). */
    transactionsExportUrl: (mode: Mode, q: Omit<TransactionQuery, "startingAfter" | "limit">) =>
      dash("/transactions/export", mode, {
        status: q.status,
        network: q.network,
        q: q.q,
      }),
    transaction: (mode: Mode, id: string) =>
      request<TransactionDetail>("GET", dash(`/transactions/${enc(id)}`, mode)),
    payment: (mode: Mode, id: string) => request<PaymentDetail>("GET", dash(`/payments/${enc(id)}`, mode)),
    createPayment: (input: CreatePaymentInput) =>
      request<Payment>("POST", dash("/payments", input.mode), input),
    /** Deactivates an awaiting_payment payment (canceled); 409 otherwise. */
    cancelPayment: (mode: Mode, id: string) =>
      request<Payment>("POST", dash(`/payments/${enc(id)}/cancel`, mode)),
    acceptPayment: (mode: Mode, id: string) =>
      request<Payment>("POST", dash(`/payments/${enc(id)}/accept`, mode)),
    /** Reads balances on every enabled EVM network: slow, so only on request. */
    strayFunds: (mode: Mode, id: string) =>
      request<{ data: StrayFunds[] }>("GET", dash(`/payments/${enc(id)}/stray-funds`, mode)),
    /** Sends `token` ("native" or a contract) at `address` on `network` to the merchant's wallet. */
    recoverFunds: (mode: Mode, id: string, body: { address: string; network: string; token: string }) =>
      request<Payment>("POST", dash(`/payments/${enc(id)}/recover`, mode), body),
  };
}

export type ProfileApi = ReturnType<typeof profileApi>;
